import { Routes } from '@angular/router';
import {
    getCompanyById,
    getCompanyDashboards,
    getDailyGraphDashboard,
    getMonthDashboard,
    getOperationsByUuid,
    getReferralsStatsByUuid,
    getReportsStatsByUuid,
    getStopWalletById,
    getStopWalletTransactionById,
    getSubscriptionStatsByUuid,
    getTransactionStatsByUuid,
    inputAccountEntityMerchant,
    conditionalResolver
} from "./resolvers";
import { rolesGuard } from "./partials/guards/roles.guard";
import { caseManagementRoutes } from "./pages/case-management/case-management.routes";
import moment from "moment";

const blockingRoutes: Routes = [
    {
        path: 'blocking',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
        children: [
            {
                path: 'monitoring-list',
                loadComponent: () => import('./pages/blocking/blocking-monitoring-list/blocking-monitoring-list.component').then(c => c.BlockingMonitoringListComponent)
            }
        ],
    },
    {
        path: 'blocking/:id',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
        resolve: {
            stopWalletData: getStopWalletById,
            stopWalletTransactionData: getStopWalletTransactionById
        },
        loadComponent: () => import('./pages/blocking/blocking-case/blocking-case.component').then(c => c.BlockingCaseComponent)
    },
];

const transactionRoutes: Routes = [
    {
        path: 'transactions',
        canActivate: [rolesGuard(['SUPER_USER', 'SALES', 'GENERAL_MANAGER', 'MANAGER'])],
        loadComponent: () => import('./pages/transactions/transactions.component').then(c => c.TransactionsComponent)
    }
];

const reportsRoutes: Routes = [
    {
        path: 'reports',
        canActivate: [rolesGuard(['SUPER_USER', 'SALES', 'GENERAL_MANAGER', 'MANAGER'])],
        loadComponent: () => import('./pages/reports/reports.component').then(c => c.ReportsComponent)
    }
];

const merchantRoutes: Routes = [
    {
        path: 'merchants',
        canActivate: [rolesGuard(['SUPER_USER', 'SALES', 'GENERAL_MANAGER', 'MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/merchants/merchant-list/merchant-list.component').then(c => c.MerchantListComponent)
    },
    {
        path: 'merchant/:uuid',
        canActivate: [rolesGuard(['SUPER_USER', 'SALES', 'GENERAL_MANAGER', 'MANAGER', 'WALLET_STOP_MANAGER'])],
        resolve: {
            inputAccountEntity: inputAccountEntityMerchant,

            userSubscriptionStats: conditionalResolver(
                ['SUPER_USER', 'SALES'],
                getSubscriptionStatsByUuid
            ),
            inputUserOperations: conditionalResolver(
                ['SUPER_USER', 'SALES'],
                getOperationsByUuid
            ),
            userReportsStats: conditionalResolver(
                ['SUPER_USER', 'SALES', 'GENERAL_MANAGER', 'MANAGER'],
                getReportsStatsByUuid
            ),
            userTransactionsStats: conditionalResolver(
                ['SUPER_USER', 'SALES', 'GENERAL_MANAGER', 'MANAGER'],
                getTransactionStatsByUuid
            ),
            userReferralsStats:
            conditionalResolver(
                ['SUPER_USER', 'SALES'],
                getReferralsStatsByUuid
            ),
        },
        loadComponent: () => import('./pages/merchants/merchant/merchant.component').then(c => c.MerchantComponent)
    }
];

const analyticsRoutes: Routes = [
    {
        path: 'analytics',
        resolve: {
            initFrom: () => moment().subtract(7, 'days').format(),
            initTo: () => moment().format(),
            initDailyGraphDashboard: getDailyGraphDashboard(moment().subtract(7, 'days').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')),
            iniMonthDashboard: getMonthDashboard(moment().subtract(7, 'days').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')),
        },
        canActivate: [rolesGuard(['SUPER_USER', 'SALES'])],
        loadComponent: () => import('./pages/analytics/analytics.component').then(c => c.AnalyticsComponent)
    },
];

const complianceRoutes: Routes = [
    // {
    //     path: 'compliance',
    //     canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
    //     loadComponent: () => import('./pages/compliance/compliance-list/compliance-list.component').then(c => c.ComplianceListComponent)
    // },
];

const companyRoutes: Routes = [
    {
        path: 'company-list',
        canActivate: [rolesGuard(['SUPER_USER', 'SALES', 'GENERAL_MANAGER', 'MANAGER'])],
        loadComponent: () => import('./pages/company/company-list/company-list.component').then(c => c.CompanyListComponent),
    },
    {
        path: 'company/:uuid',
        canActivate: [rolesGuard(['SUPER_USER', 'SALES', 'GENERAL_MANAGER', 'MANAGER'])],
        resolve: {
            initResponse: getCompanyById,
        },
        loadComponent: () => import('./pages/company/company-page/company-page.component').then(c => c.CompanyPageComponent),
    },
];

const errorRoutes: Routes = [
    {
        path: '404',
        loadComponent: () => import('./pages/errors/not-found/not-found.component').then(c => c.NotFoundComponent)
    },
    {
        path: 'something-went-wrong',
        loadComponent: () => import('./pages/errors/something-went-wrong/something-went-wrong.component').then(c => c.SomethingWentWrongComponent)
    },
    {
        path: 'access-denied',
        loadComponent: () => import('./pages/errors/access-denied/access-denied.component').then(c => c.AccessDeniedComponent)
    },
];

const clusterRoutes: Routes = [
    {
        path: 'compliance-companies',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/compliance-companies/compliance-companies.component').then(c => c.ComplianceCompaniesComponent),
    },
    {
        path: 'cluster-transactions',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/cluster-transactions/cluster-transactions.component').then(c => c.ClusterTransactionsComponent)
    },
    {
        path: 'cluster-subscriptions',
        canActivate: [rolesGuard(['SUPER_USER'])],
        loadComponent: () => import('./pages/cluster-subscriptions/cluster-subscriptions.component').then(c => c.ClusterSubscriptionsComponent)
    },

    {
        path: 'transactions-by-cluster-wallet/:id/:address',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/transactions-by-cluster-wallet/transactions-by-cluster-wallet.component').then(c => c.TransactionsByClusterWalletComponent)
    },
    {
        path: 'company-transactions-by-cluster-wallet/:id/:address',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/company/company-page/components/company-transactions-by-cluster-wallet/company-transactions-by-cluster-wallet.component').then(c => c.CompanyTransactionsByClusterWalletComponent)
    },
    {
        path: 'account-transactions-by-cluster-wallet/:id/:address',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/merchants/merchant/components/account-transactions-by-cluster-wallet/account-transactions-by-cluster-wallet.component').then(c => c.AccountTransactionsByClusterWalletComponent)
    }
];

const graphExplorerRoutes: Routes = [
    {
        path: 'graph-reactor',
        canActivate: [rolesGuard(['SUPER_USER', 'MANAGER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/graph-reactor/graph-library.component').then(c => c.GraphLibraryComponent)
    },
    {
        path: 'graph-reactor/:id',
        canActivate: [rolesGuard(['SUPER_USER', 'MANAGER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/graph-reactor/graph-reactor.component').then(c => c.GraphReactorComponent)
    }
];

const leaRoutes: Routes = [
    {
        path: 'lea-users',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/lea/lea-user-list/lea-user-list.component').then(c => c.LeaUserListComponent)
    },
    {
        path: 'lea-cases',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/lea/lea-case-list/lea-case-list.component').then(c => c.LeaCaseListComponent)
    },
    {
        path: 'lea-case/:id',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/lea/lea-case/lea-case.component').then(c => c.LeaCaseComponent)
    }
];

const invoiceRoutes: Routes = [
    {
        path: 'invoices',
        canActivate: [rolesGuard(['SUPER_USER', 'SALES'])],
        loadComponent: () => import('./pages/invoices/invoice-list/invoice-list.component').then(c => c.InvoiceListComponent)
    }
];

const paymentRoutes: Routes = [
    {
        path: 'payments',
        canActivate: [rolesGuard(['SUPER_USER', 'SALES'])],
        loadComponent: () => import('./pages/payments/payment-list/payment-list.component').then(c => c.PaymentListComponent)
    }
];


const adminRoutes: Routes = [
    {
        path: 'settings',
        canActivate: [rolesGuard(['SUPER_USER'])],
        loadComponent: () => import('./pages/settings/settings.component').then(c => c.SettingsComponent),
        children: [
            { path: '', pathMatch: 'full', redirectTo: 'project-settings' },
            {
                path: 'project-settings',
                loadComponent: () => import('./pages/settings/project-settings/project-settings.component').then(c => c.ProjectSettingsComponent)
            },
            {
                path: 'admins',
                loadComponent: () => import('./pages/admins/admin-list/admin-list.component').then(c => c.AdminListComponent)
            },
            {
                path: 'recovery-partner',
                loadComponent: () => import('./pages/settings/recovery-partner/recovery-partner.component').then(c => c.RecoveryPartnerComponent)
            },
            {
                path: 'plan-templates',
                loadComponent: () => import('./pages/settings/plan-templates/plan-templates.component').then(c => c.PlanTemplatesComponent)
            },
            {
                path: 'providers',
                loadComponent: () => import('./pages/settings/providers/providers.component').then(c => c.ProvidersComponent)
            },
            {
                path: 'markup-channels',
                loadComponent: () => import('./pages/settings/markup-channels/markup-channels.component').then(c => c.MarkupChannelsComponent)
            },
            {
                path: 'surveys',
                loadComponent: () => import('./pages/settings/surveys/surveys.component').then(c => c.SurveysComponent)
            },
            {
                path: 'survey-responses',
                loadComponent: () => import('./pages/settings/surveys/survey-responses/survey-responses.component').then(c => c.SurveyResponsesComponent)
            },
            {
                path: 'logs',
                loadComponent: () => import('./pages/settings/logs/logs.component').then(c => c.LogsComponent)
            },
            {
                path: 'integration-keys',
                loadComponent: () => import('./pages/settings/integration-keys/integration-keys.component').then(c => c.IntegrationKeysComponent)
            },
            {
                path: 'missing-deposit-addresses',
                loadComponent: () => import('./pages/settings/missing-deposit-addresses/missing-deposit-addresses.component').then(c => c.MissingDepositAddressesComponent)
            }
        ]
    }
];

const securityRoutes: Routes = [
    {
        path: 'security',
        canActivate: [rolesGuard(['SUPER_USER', 'GENERAL_MANAGER', 'MANAGER', 'SALES', 'WALLET_STOP_MANAGER'])],
        loadComponent: () => import('./pages/security/security.component').then(c => c.SecurityComponent)
    }
];

export const routes: Routes = [
    {
        path: 'login',
        loadChildren: () => import('./pages/auth/auth.module').then(m => m.AuthModule)
    },
    {
        path: '',
        loadComponent: () => import('./layout/layout.component').then(c => c.LayoutComponent),
        children: [
            ...merchantRoutes,
            ...companyRoutes,
            ...transactionRoutes,
            ...reportsRoutes,
            ...paymentRoutes,
            ...analyticsRoutes,
            ...complianceRoutes,
            ...clusterRoutes,
            ...graphExplorerRoutes,
            ...blockingRoutes,
            ...leaRoutes,
            ...invoiceRoutes,
            ...caseManagementRoutes,
            ...adminRoutes,
            ...securityRoutes,
            ...errorRoutes,
            {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./pages/start/start.component').then(c => c.StartComponent)
            }
        ]
    },
    {
        path: '**',
        redirectTo: '404'
    }
];
